/**
 * System report filters, ported from system-report.js renderTable and
 * renderFilteredFlatList: any active filter switches the table from the
 * hierarchy to a flat list of matching rows shown as "Parent » Name".
 */
import type { StatusColour } from '../../types';

export type ReportStatusFilter = 'all' | 'critical' | 'warning' | 'operational';

export interface ReportFilters {
  search: string;
  status: ReportStatusFilter;
  /** 'all' or a category name (Mechanical, RF Electronics, Digital, Power, Passive, Structural, Other). */
  category: string;
}

export const DEFAULT_FILTERS: ReportFilters = { search: '', status: 'all', category: 'all' };

export const REPORT_CATEGORIES = ['Mechanical', 'RF Electronics', 'Digital', 'Power', 'Passive', 'Structural', 'Other'];

export interface FilterableRow {
  name: string;
  parentLabel?: string;
  /** Derived level or the previous status strings (OPERATIONAL, WARNING, DEGRADED, CRITICAL, OFFLINE, ONLINE). */
  level: string;
  colour: StatusColour | null;
  category: string;
}

/** True when the table must render the flat list instead of the hierarchy. */
export function filtersActive(f: ReportFilters): boolean {
  return f.search.trim() !== '' || f.status !== 'all' || f.category !== 'all';
}

export function matchesStatus(row: FilterableRow, status: ReportStatusFilter): boolean {
  if (status === 'all') return true;
  const level = row.level.toUpperCase();
  if (status === 'critical') return level === 'CRITICAL' || level === 'OFFLINE' || row.colour === 'red';
  if (status === 'warning') return level === 'WARNING' || level === 'DEGRADED' || row.colour === 'yellow';
  return level === 'OPERATIONAL' || level === 'ONLINE' || row.colour === 'green';
}

/** Rows matching every active filter: search on name or parent, status, category. */
export function filterRows<T extends FilterableRow>(rows: T[], f: ReportFilters): T[] {
  const q = f.search.trim().toLowerCase();
  return rows.filter((row) => {
    if (q) {
      const nameMatch = row.name.toLowerCase().includes(q);
      const parentMatch = (row.parentLabel ?? '').toLowerCase().includes(q);
      if (!nameMatch && !parentMatch) return false;
    }
    if (!matchesStatus(row, f.status)) return false;
    if (f.category !== 'all' && row.category !== f.category) return false;
    return true;
  });
}
